import {useCallback, useState} from 'react';

import type {ConditionGroup} from 'studies/typed/shared/useExperimentDesignState';

import {
  getProcedureRunDesignFlowchartEdgeId,
  parseProcedureRunDesignFlowchartEdgeId,
} from './ProcedureRunDesignFlowchartEdge';
import type {FlowchartEdgeSpec} from './ProcedureRunDesignFlowchartLayoutUtils';
import {parseProcedureRunDesignFlowchartNodeId} from './ProcedureRunDesignFlowchartNodeIdUtils';

export interface UseProcedureRunDesignEdgeEditsResult {
  /** Edges drawn this session, pruned to endpoints that still exist. */
  drawnEdgeSpecs: Array<FlowchartEdgeSpec>;
  /** Ids of edges removed this session, pruned to endpoints that still exist. */
  removedEdgeIds: ReadonlySet<string>;
  /** Records a drawn edge. */
  addDrawnEdge: (spec: FlowchartEdgeSpec) => void;
  /** Removes an edge by id, dropping any matching draft. */
  removeEdge: (edgeId: string) => void;
  /** Clears all edge edits. */
  resetEdits: () => void;
  /** Combines drawn edges into the derived list, then drops removed ones. */
  applyEdits: (derivedEdgeSpecs: ReadonlyArray<FlowchartEdgeSpec>) => Array<FlowchartEdgeSpec>;
}

function edgeSpecId(spec: FlowchartEdgeSpec): string {
  return getProcedureRunDesignFlowchartEdgeId(spec.sourceNodeId, spec.targetNodeId);
}

/** Whether a node id still maps to an existing condition row and replicate. */
function isEndpointResolvable(nodeId: string, conditions: ReadonlyArray<ConditionGroup>): boolean {
  const {conditionIndex, replicateIndex} = parseProcedureRunDesignFlowchartNodeId(nodeId);
  const condition = conditions[conditionIndex];
  return condition != null && replicateIndex < condition.numReplicates;
}

/**
 * Keeps only edges whose endpoints still exist, so removing a condition can't leave a dangling edge.
 */
export function pruneDrawnEdgeSpecs(
  drawnEdgeSpecs: ReadonlyArray<FlowchartEdgeSpec>,
  conditions: ReadonlyArray<ConditionGroup>
): Array<FlowchartEdgeSpec> {
  return drawnEdgeSpecs.filter(
    (spec) =>
      isEndpointResolvable(spec.sourceNodeId, conditions) &&
      isEndpointResolvable(spec.targetNodeId, conditions)
  );
}

/**
 * Keeps only removed ids whose endpoints still exist, mirroring draft pruning so removing a condition
 * can't strand a removed id.
 */
export function pruneRemovedEdgeIds(
  removedEdgeIds: ReadonlySet<string>,
  conditions: ReadonlyArray<ConditionGroup>
): ReadonlySet<string> {
  const pruned = new Set<string>();
  for (const edgeId of removedEdgeIds) {
    const {sourceNodeId, targetNodeId} = parseProcedureRunDesignFlowchartEdgeId(edgeId);
    if (isEndpointResolvable(sourceNodeId, conditions) && isEndpointResolvable(targetNodeId, conditions)) {
      pruned.add(edgeId);
    }
  }
  return pruned;
}

/** Appends drawn specs to derived specs, dropping any drawn spec already present in the derived set. */
export function combineEdgeSpecs(
  derivedEdgeSpecs: ReadonlyArray<FlowchartEdgeSpec>,
  drawnEdgeSpecs: ReadonlyArray<FlowchartEdgeSpec>
): Array<FlowchartEdgeSpec> {
  const seenIds = new Set(derivedEdgeSpecs.map((spec) => edgeSpecId(spec)));
  const combined = [...derivedEdgeSpecs];
  for (const spec of drawnEdgeSpecs) {
    const id = edgeSpecId(spec);
    if (!seenIds.has(id)) {
      seenIds.add(id);
      combined.push(spec);
    }
  }
  return combined;
}

/** Drops specs whose edge id is in the removed set, including derived edges. */
export function applyEdgeRemovals(
  specs: ReadonlyArray<FlowchartEdgeSpec>,
  removedEdgeIds: ReadonlySet<string>
): Array<FlowchartEdgeSpec> {
  if (removedEdgeIds.size === 0) {
    return [...specs];
  }
  return specs.filter((spec) => !removedEdgeIds.has(edgeSpecId(spec)));
}

/**
 * Owns the run tab's in-session edge edits: drafts added and existing edges removed. Edits are pruned as
 * conditions change, merged into the save payload via `applyEdits`, and cleared only on reset. Removing
 * records the edge id rather than a draft, so an edge with no draft is still left out of the next save.
 */
export function useProcedureRunDesignEdgeEdits(
  conditions: ReadonlyArray<ConditionGroup>
): UseProcedureRunDesignEdgeEditsResult {
  const [drawnEdgeSpecs, setDrawnEdgeSpecs] = useState<Array<FlowchartEdgeSpec>>([]);
  const [removedEdgeIds, setRemovedEdgeIds] = useState<ReadonlySet<string>>(() => new Set());

  // Reconcile against the current conditions during render (React's adjust-state-on-prop-change
  // pattern). Dropping edits whose endpoints no longer resolve keeps a stale edit from reviving onto a
  // shifted node id. Reuse the existing reference when nothing changed so downstream memos don't churn.
  const reconciledEdgeSpecs = pruneDrawnEdgeSpecs(drawnEdgeSpecs, conditions);
  const nextEdgeSpecs =
    reconciledEdgeSpecs.length === drawnEdgeSpecs.length ? drawnEdgeSpecs : reconciledEdgeSpecs;
  if (nextEdgeSpecs !== drawnEdgeSpecs) {
    setDrawnEdgeSpecs(nextEdgeSpecs);
  }

  const reconciledRemovedEdgeIds = pruneRemovedEdgeIds(removedEdgeIds, conditions);
  const nextRemovedEdgeIds =
    reconciledRemovedEdgeIds.size === removedEdgeIds.size ? removedEdgeIds : reconciledRemovedEdgeIds;
  if (nextRemovedEdgeIds !== removedEdgeIds) {
    setRemovedEdgeIds(nextRemovedEdgeIds);
  }

  const addDrawnEdge = useCallback((spec: FlowchartEdgeSpec) => {
    const id = edgeSpecId(spec);
    setDrawnEdgeSpecs((current) =>
      current.some((existing) => edgeSpecId(existing) === id) ? current : [...current, spec]
    );
    setRemovedEdgeIds((current) => {
      if (!current.has(id)) {
        return current;
      }
      const next = new Set(current);
      next.delete(id);
      return next;
    });
  }, []);

  const removeEdge = useCallback((edgeId: string) => {
    setRemovedEdgeIds((current) => (current.has(edgeId) ? current : new Set(current).add(edgeId)));
    setDrawnEdgeSpecs((current) => {
      const next = current.filter((spec) => edgeSpecId(spec) !== edgeId);
      return next.length === current.length ? current : next; // Preserve existing reference if no change
    });
  }, []);

  const resetEdits = useCallback(() => {
    setDrawnEdgeSpecs([]);
    setRemovedEdgeIds(new Set());
  }, []);

  const applyEdits = useCallback(
    (derivedEdgeSpecs: ReadonlyArray<FlowchartEdgeSpec>): Array<FlowchartEdgeSpec> =>
      applyEdgeRemovals(combineEdgeSpecs(derivedEdgeSpecs, nextEdgeSpecs), nextRemovedEdgeIds),
    [nextEdgeSpecs, nextRemovedEdgeIds]
  );

  return {
    drawnEdgeSpecs: nextEdgeSpecs,
    removedEdgeIds: nextRemovedEdgeIds,
    addDrawnEdge,
    removeEdge,
    resetEdits,
    applyEdits,
  };
}
